import {
  Component,
  signal,
  computed,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormDropdownService } from '../../../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../../../core/services/geography.service';
import { ProfessionalsService } from '../../../../../core/services/professionals.service';
import { UserSessionService } from '../../../../../core/services/mock-auth.service';
import { ProfessionalsWizardService } from '../../../../services/professionals-wizard.service';
import { ProfessionalsDataService } from '../../../../services/professionals-data.service';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ProfessionalDropdownOption } from '../../../../../core/models/professional.models';
import { hasMeaningfulText } from '../../../../../core/validators/text-value.utils';
import { ValidationErrorSection } from '../../../validation-error-summary/validation-error-summary.model';

interface Collaborator {
  id: number;
  surname: string;
  name: string;
  fiscalCode: string;
  registrationNumber: string;
  claimsSystemAccess: boolean;
}

@Component({
  selector: 'app-hail-provider-form',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    RadioButtonModule,
    MultiSelectModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: './hail-provider-form.component.html',
  styleUrl: './shared-form-styles.scss',
})
export class HailProviderFormComponent implements OnInit {
  @ViewChildren('firstInput') firstInputs!: QueryList<ElementRef>;

  readonly hasMeaningfulText = hasMeaningfulText;

  @Output() onSave = new EventEmitter<any>();
  @Output() onSaveDraft = new EventEmitter<any>();
  @Output() validationFailed = new EventEmitter<ValidationErrorSection[]>();

  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);
  private professionalsService = inject(ProfessionalsService);
  private mockAuthService = inject(UserSessionService);
  private titleService = inject(Title);
  private messageService = inject(MessageService);
  private professionalsWizardService = inject(ProfessionalsWizardService);
  private professionalsDataService = inject(ProfessionalsDataService);

  isReadOnly = computed(() => this.professionalsDataService.isReadOnly());

  // Live region message for screen readers
  liveRegionMessage = signal<string>('');

  // Form submission tracking
  formSubmitted = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  public touchedFields = new Set<string>();

  // Collaborators
  collaborators = signal<Collaborator[]>([]);
  private collaboratorIdCounter = 1;

  // --- Section 1: INFORMAZIONI PROVIDER GRANDINE ---
  companiesWorkedFor = signal<string>(''); // Imprese per le quali lavora (#1 - NO)
  synergyInternationalCompanies = signal<number | null>(null); // Sinergie internazionali (#2 - SI)
  synergyNationalCompanies = signal<number | null>(null); // Sinergie nazionali (#3 - NO)
  numberOfBranches = signal<number | null>(null); // Numero filiali (#4 - SI)
  isTeslaApproved = signal<boolean | null>(null); // Tesla Approved (#5 - SI)
  electricNetworkPercentage = signal<number | null>(null); // Network elettrico % (#6 - SI)
  numberOfOwnedBodyShops = signal<number | null>(null); // Carrozzerie di proprieta (#7 - SI)
  numberOfEmployees = signal<number | null>(null); // N. Dipendenti (#8 - SI)
  numberOfHailTechnicians = signal<number | null>(null); // Tirabolli dipendenti (#9 - SI)
  hasTrainingCourses = signal<boolean | null>(null); // Corsi formativi (#10 - NO)
  hasEstimateSoftware = signal<boolean | null>(null); // Software preventivi (#11 - SI)
  hasInternalPartsProcurement = signal<boolean | null>(null); // Centrale acquisti ricambi (#12 - SI)
  operativeTerritory = signal<string[]>([]); // Territorio operativo (#13 - SI)

  // Readonly: etichette dei territori operativi selezionati, risolte dalle
  // province (il p-multiselect disabilitato non mostra i chip selezionati).
  operativeTerritoryLabels = computed(() => {
    const options = this.provinceOptions();
    return this.operativeTerritory().map(
      (value) => options.find((option) => option.value === value)?.label ?? value,
    );
  });

  // --- Section 2: SERVIZI ---
  hasCreditAssignment = signal<boolean | null>(null); // Cessione di credito (#21)
  hasMinimumAbsorption = signal<boolean | null>(null); // Assorbimento minimo (#22)
  hasDeductibleAbsorption = signal<boolean | null>(null); // Assorbimento scoperto (#23)
  hasPriorityGlassRepair = signal<boolean | null>(null); // Riparazione prioritaria cristalli (#24)
  hasGlassRepairPartner = signal<boolean | null>(null); // Partner riparazione cristalli (#25)
  glassRepairPartnerName = signal<string>(''); // Ragione sociale partner cristalli (#26 - obbligatorio se #25 = SI)
  hasPickupDelivery = signal<boolean | null>(null); // Pick up & delivery (#27)
  hasTowTruck = signal<boolean | null>(null); // Carro attrezzi (#28)
  hasFreeReplacementCar = signal<boolean | null>(null); // Auto sostitutiva gratuita (#29)
  hasInteriorCleaning = signal<boolean | null>(null); // Pulizia interna (#30)
  hasExteriorCleaning = signal<boolean | null>(null); // Pulizia esterna (#31)
  hasSanitization = signal<boolean | null>(null); // Sanificazione (#32 - NO)
  hasRepairTrackingForAgencies = signal<boolean | null>(null); // Tracking per agenzie (#33)
  hasRepairTrackingForClients = signal<boolean | null>(null); // Tracking per clienti (#34)
  coldRepairWarrantyDuration = signal<string>(''); // Durata garanzia riparazioni freddo (#35)
  numberOfScanners = signal<number | null>(null); // Scanner per rilevazione danni (#36)
  hasMobileRepairService = signal<boolean | null>(null); // Servizio a domicilio riparazioni freddo (#37)
  numberOfAffiliatedBodyShops = signal<number | null>(null); // Carrozzerie affiliate (BE field, not shown in form)

  // --- Section 3: CONTATTI ---
  agencyContactEmail = signal<string>(''); // Contatto agenzie (mail) (#38)
  agencyContactPhone = signal<string>(''); // Contatto agenzie (telefono) (#39)
  managementContactEmail = signal<string>(''); // Contatto direzione (mail) (#40)
  managementContactPhone = signal<string>(''); // Contatto direzione (telefono) (#41)
  surveyOfficeContactEmail = signal<string>(''); // Contatto studi peritali (mail) (#42)
  surveyOfficeContactPhone = signal<string>(''); // Contatto studi peritali (telefono) (#43)
  thirdPartyCommercialEmail = signal<string>(''); // Mail soggetti terzi (#44)
  claimAssignmentNotificationEmail = signal<string>(''); // Mail notifica incarico sinistro (#45)

  // Geography dropdowns
  regionOptions = signal<ProfessionalDropdownOption[]>([]);
  streetTypeOptions = signal<ProfessionalDropdownOption[]>([]);
  provinceOptions = signal<ProfessionalDropdownOption[]>([
    { label: 'Agrigento (AG)', value: 'AG' }, { label: 'Alessandria (AL)', value: 'AL' },
    { label: 'Ancona (AN)', value: 'AN' }, { label: 'Aosta (AO)', value: 'AO' },
    { label: 'Arezzo (AR)', value: 'AR' }, { label: 'Ascoli Piceno (AP)', value: 'AP' },
    { label: 'Asti (AT)', value: 'AT' }, { label: 'Avellino (AV)', value: 'AV' },
    { label: 'Bari (BA)', value: 'BA' }, { label: 'Barletta-Andria-Trani (BT)', value: 'BT' },
    { label: 'Belluno (BL)', value: 'BL' }, { label: 'Benevento (BN)', value: 'BN' },
    { label: 'Bergamo (BG)', value: 'BG' }, { label: 'Biella (BI)', value: 'BI' },
    { label: 'Bologna (BO)', value: 'BO' }, { label: 'Bolzano (BZ)', value: 'BZ' },
    { label: 'Brescia (BS)', value: 'BS' }, { label: 'Brindisi (BR)', value: 'BR' },
    { label: 'Cagliari (CA)', value: 'CA' }, { label: 'Caltanissetta (CL)', value: 'CL' },
    { label: 'Campobasso (CB)', value: 'CB' }, { label: 'Caserta (CE)', value: 'CE' },
    { label: 'Catania (CT)', value: 'CT' }, { label: 'Catanzaro (CZ)', value: 'CZ' },
    { label: 'Chieti (CH)', value: 'CH' }, { label: 'Como (CO)', value: 'CO' },
    { label: 'Cosenza (CS)', value: 'CS' }, { label: 'Cremona (CR)', value: 'CR' },
    { label: 'Crotone (KR)', value: 'KR' }, { label: 'Cuneo (CN)', value: 'CN' },
    { label: 'Enna (EN)', value: 'EN' }, { label: 'Fermo (FM)', value: 'FM' },
    { label: 'Ferrara (FE)', value: 'FE' }, { label: 'Firenze (FI)', value: 'FI' },
    { label: 'Foggia (FG)', value: 'FG' }, { label: 'Forli-Cesena (FC)', value: 'FC' },
    { label: 'Frosinone (FR)', value: 'FR' }, { label: 'Genova (GE)', value: 'GE' },
    { label: 'Gorizia (GO)', value: 'GO' }, { label: 'Grosseto (GR)', value: 'GR' },
    { label: 'Imperia (IM)', value: 'IM' }, { label: 'Isernia (IS)', value: 'IS' },
    { label: "L'Aquila (AQ)", value: 'AQ' }, { label: 'La Spezia (SP)', value: 'SP' },
    { label: 'Latina (LT)', value: 'LT' }, { label: 'Lecce (LE)', value: 'LE' },
    { label: 'Lecco (LC)', value: 'LC' }, { label: 'Livorno (LI)', value: 'LI' },
    { label: 'Lodi (LO)', value: 'LO' }, { label: 'Lucca (LU)', value: 'LU' },
    { label: 'Macerata (MC)', value: 'MC' }, { label: 'Mantova (MN)', value: 'MN' },
    { label: 'Massa-Carrara (MS)', value: 'MS' }, { label: 'Matera (MT)', value: 'MT' },
    { label: 'Messina (ME)', value: 'ME' }, { label: 'Milano (MI)', value: 'MI' },
    { label: 'Modena (MO)', value: 'MO' }, { label: 'Monza e Brianza (MB)', value: 'MB' },
    { label: 'Napoli (NA)', value: 'NA' }, { label: 'Novara (NO)', value: 'NO' },
    { label: 'Nuoro (NU)', value: 'NU' }, { label: 'Oristano (OR)', value: 'OR' },
    { label: 'Padova (PD)', value: 'PD' }, { label: 'Palermo (PA)', value: 'PA' },
    { label: 'Parma (PR)', value: 'PR' }, { label: 'Pavia (PV)', value: 'PV' },
    { label: 'Perugia (PG)', value: 'PG' }, { label: 'Pesaro e Urbino (PU)', value: 'PU' },
    { label: 'Pescara (PE)', value: 'PE' }, { label: 'Piacenza (PC)', value: 'PC' },
    { label: 'Pisa (PI)', value: 'PI' }, { label: 'Pistoia (PT)', value: 'PT' },
    { label: 'Pordenone (PN)', value: 'PN' }, { label: 'Potenza (PZ)', value: 'PZ' },
    { label: 'Prato (PO)', value: 'PO' }, { label: 'Ragusa (RG)', value: 'RG' },
    { label: 'Ravenna (RA)', value: 'RA' }, { label: 'Reggio Calabria (RC)', value: 'RC' },
    { label: 'Reggio Emilia (RE)', value: 'RE' }, { label: 'Rieti (RI)', value: 'RI' },
    { label: 'Rimini (RN)', value: 'RN' }, { label: 'Roma (RM)', value: 'RM' },
    { label: 'Rovigo (RO)', value: 'RO' }, { label: 'Salerno (SA)', value: 'SA' },
    { label: 'Sassari (SS)', value: 'SS' }, { label: 'Savona (SV)', value: 'SV' },
    { label: 'Siena (SI)', value: 'SI' }, { label: 'Siracusa (SR)', value: 'SR' },
    { label: 'Sondrio (SO)', value: 'SO' }, { label: 'Sud Sardegna (SU)', value: 'SU' },
    { label: 'Taranto (TA)', value: 'TA' }, { label: 'Teramo (TE)', value: 'TE' },
    { label: 'Terni (TR)', value: 'TR' }, { label: 'Torino (TO)', value: 'TO' },
    { label: 'Trapani (TP)', value: 'TP' }, { label: 'Trento (TN)', value: 'TN' },
    { label: 'Treviso (TV)', value: 'TV' }, { label: 'Trieste (TS)', value: 'TS' },
    { label: 'Udine (UD)', value: 'UD' }, { label: 'Varese (VA)', value: 'VA' },
    { label: 'Venezia (VE)', value: 'VE' }, { label: 'Verbano-Cusio-Ossola (VB)', value: 'VB' },
    { label: 'Vercelli (VC)', value: 'VC' }, { label: 'Verona (VR)', value: 'VR' },
    { label: 'Vibo Valentia (VV)', value: 'VV' }, { label: 'Vicenza (VI)', value: 'VI' },
    { label: 'Viterbo (VT)', value: 'VT' },
  ]);

  protected isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Validation computed
  isFormValid = computed(() => {
    // Section 1 - required fields
    const section1Valid =
      this.synergyInternationalCompanies() !== null &&
      this.synergyNationalCompanies() !== null &&
      this.numberOfBranches() !== null &&
      this.isTeslaApproved() !== null &&
      this.electricNetworkPercentage() !== null &&
      this.numberOfOwnedBodyShops() !== null &&
      this.numberOfEmployees() !== null &&
      this.numberOfHailTechnicians() !== null &&
      this.hasEstimateSoftware() !== null &&
      this.hasInternalPartsProcurement() !== null &&
      this.operativeTerritory().length > 0;

    // Section 2 - required fields
    const section2Valid =
      this.hasCreditAssignment() !== null &&
      this.hasMinimumAbsorption() !== null &&
      this.hasDeductibleAbsorption() !== null &&
      this.hasPriorityGlassRepair() !== null &&
      this.hasGlassRepairPartner() !== null &&
      (this.hasGlassRepairPartner() !== true || !!this.glassRepairPartnerName()) &&
      this.hasPickupDelivery() !== null &&
      this.hasTowTruck() !== null &&
      this.hasFreeReplacementCar() !== null &&
      this.hasInteriorCleaning() !== null &&
      this.hasExteriorCleaning() !== null &&
      this.hasRepairTrackingForAgencies() !== null &&
      this.hasRepairTrackingForClients() !== null &&
      !!this.coldRepairWarrantyDuration() &&
      this.numberOfScanners() !== null &&
      this.hasMobileRepairService() !== null;

    // Section 3 - all contacts required (email fields con validazione formato)
    const section3Valid =
      !!this.agencyContactEmail() && this.isValidEmail(this.agencyContactEmail()) &&
      !!this.agencyContactPhone() &&
      !!this.managementContactEmail() && this.isValidEmail(this.managementContactEmail()) &&
      !!this.managementContactPhone() &&
      !!this.surveyOfficeContactEmail() && this.isValidEmail(this.surveyOfficeContactEmail()) &&
      !!this.surveyOfficeContactPhone() &&
      !!this.thirdPartyCommercialEmail() && this.isValidEmail(this.thirdPartyCommercialEmail()) &&
      !!this.claimAssignmentNotificationEmail() && this.isValidEmail(this.claimAssignmentNotificationEmail());

    return section1Valid && section2Valid && section3Valid;
  });

  ngOnInit(): void {
    this.titleService.setTitle('Portale Fiduciari - Dati Specifici Riparatore Grandine');
    this.loadDropdownOptions();
    this.loadExistingData();
  }

  private loadDropdownOptions(): void {
    // Load regions
    this.geographyService.getRegions().subscribe({
      next: (regions: any) => {
        this.regionOptions.set(
          regions.map((r: any) => ({ label: r.name, value: r.name }))
        );
      },
      error: (err: any) => console.error('Error loading regions:', err),
    });

    // Load street types (using static data)
    this.streetTypeOptions.set([
      { label: 'Via', value: 'Via' },
      { label: 'Viale', value: 'Viale' },
      { label: 'Piazza', value: 'Piazza' },
      { label: 'Corso', value: 'Corso' },
      { label: 'Largo', value: 'Largo' },
      { label: 'Vicolo', value: 'Vicolo' },
    ]);
  }

  markFieldAsTouched(fieldName: string): void {
    this.touchedFields.add(fieldName);
  }

  createEmptyCollaborator(): Collaborator {
    return {
      id: this.collaboratorIdCounter++,
      surname: '',
      name: '',
      fiscalCode: '',
      registrationNumber: '',
      claimsSystemAccess: false,
    };
  }

  addCollaborator(): void {
    this.collaborators.update((curr) => [...curr, this.createEmptyCollaborator()]);
    const count = this.collaborators().length;
    this.liveRegionMessage.set(`Collaboratore ${count} aggiunto`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      const newInput = inputs[inputs.length - 1];
      if (newInput) newInput.nativeElement.focus();
    }, 100);
  }

  removeCollaborator(id: number): void {
    const index = this.collaborators().findIndex((c) => c.id === id);
    this.collaborators.update((curr) => curr.filter((c) => c.id !== id));
    this.liveRegionMessage.set(`Collaboratore ${index + 1} rimosso`);
    setTimeout(() => this.liveRegionMessage.set(''), 1000);
    setTimeout(() => {
      const inputs = this.firstInputs.toArray();
      const targetIndex = Math.max(0, index - 1);
      const targetInput = inputs[targetIndex];
      if (targetInput) targetInput.nativeElement.focus();
    }, 100);
  }

  isValidFiscalCode(fiscalCode: string): boolean {
    if (!fiscalCode) return false;
    return /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i.test(fiscalCode);
  }

  hasCollaboratorStarted(collab: any): boolean {
    return !!(
      this.hasMeaningfulText(collab.surname) ||
      this.hasMeaningfulText(collab.name) ||
      this.hasMeaningfulText(collab.fiscalCode) ||
      this.hasMeaningfulText(collab.registrationNumber)
    );
  }

  private loadExistingData(): void {
    console.log('🔍 Loading existing hail provider data...');

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      console.warn('No professional ID found');
      return;
    }

    this.professionalsService
      .getHailProviderData(professionalId)
      .pipe(
        catchError((err) => {
          console.warn('No existing hail provider data found:', err);
          return of(null);
        })
      )
      .subscribe({
        next: (response) => {
          if (response) {
            console.log('✅ Existing hail provider data loaded:', response);
            const data = response?.data ?? response;
            this.populateFormData(data);
          } else {
            console.log('ℹ️ No existing data, starting fresh');
            // Mark step as NOT completed when no data exists
            this.professionalsWizardService.markStepCompleted(1, false);
            this.professionalsWizardService.saveState();
          }
        },
      });
  }

  populateFormData(data: any): void {
    const sd = data.specificData;
    if (!sd) return;

    // Section 1
    if (sd.companiesWorkedFor) this.companiesWorkedFor.set(sd.companiesWorkedFor);
    if (sd.synergyInternationalCompanies != null) this.synergyInternationalCompanies.set(sd.synergyInternationalCompanies);
    if (sd.synergyNationalCompanies != null) this.synergyNationalCompanies.set(sd.synergyNationalCompanies);
    if (sd.numberOfBranches != null) this.numberOfBranches.set(sd.numberOfBranches);
    if (sd.isTeslaApproved !== undefined) this.isTeslaApproved.set(sd.isTeslaApproved);
    if (sd.electricNetworkPercentage != null) this.electricNetworkPercentage.set(sd.electricNetworkPercentage);
    if (sd.numberOfOwnedBodyShops != null) this.numberOfOwnedBodyShops.set(sd.numberOfOwnedBodyShops);
    if (sd.numberOfEmployees != null) this.numberOfEmployees.set(sd.numberOfEmployees);
    if (sd.numberOfHailTechnicians != null) this.numberOfHailTechnicians.set(sd.numberOfHailTechnicians);
    if (sd.hasTrainingCourses !== undefined) this.hasTrainingCourses.set(sd.hasTrainingCourses);
    if (sd.hasEstimateSoftware !== undefined) this.hasEstimateSoftware.set(sd.hasEstimateSoftware);
    if (sd.hasInternalPartsProcurement !== undefined) this.hasInternalPartsProcurement.set(sd.hasInternalPartsProcurement);
    if (sd.operativeTerritory) this.operativeTerritory.set(Array.isArray(sd.operativeTerritory) ? sd.operativeTerritory : sd.operativeTerritory.split(',').filter((v: string) => v));

    // Section 2
    if (sd.hasCreditAssignment !== undefined) this.hasCreditAssignment.set(sd.hasCreditAssignment);
    if (sd.hasMinimumAbsorption !== undefined) this.hasMinimumAbsorption.set(sd.hasMinimumAbsorption);
    if (sd.hasDeductibleAbsorption !== undefined) this.hasDeductibleAbsorption.set(sd.hasDeductibleAbsorption);
    if (sd.hasPriorityGlassRepair !== undefined) this.hasPriorityGlassRepair.set(sd.hasPriorityGlassRepair);
    if (sd.hasGlassRepairPartner !== undefined) this.hasGlassRepairPartner.set(sd.hasGlassRepairPartner);
    if (sd.glassRepairPartnerName) this.glassRepairPartnerName.set(sd.glassRepairPartnerName);
    if (sd.hasPickupDelivery !== undefined) this.hasPickupDelivery.set(sd.hasPickupDelivery);
    if (sd.hasTowTruck !== undefined) this.hasTowTruck.set(sd.hasTowTruck);
    if (sd.hasFreeReplacementCar !== undefined) this.hasFreeReplacementCar.set(sd.hasFreeReplacementCar);
    if (sd.hasInteriorCleaning !== undefined) this.hasInteriorCleaning.set(sd.hasInteriorCleaning);
    if (sd.hasExteriorCleaning !== undefined) this.hasExteriorCleaning.set(sd.hasExteriorCleaning);
    if (sd.hasSanitization !== undefined) this.hasSanitization.set(sd.hasSanitization);
    if (sd.hasRepairTrackingForAgencies !== undefined) this.hasRepairTrackingForAgencies.set(sd.hasRepairTrackingForAgencies);
    if (sd.hasRepairTrackingForClients !== undefined) this.hasRepairTrackingForClients.set(sd.hasRepairTrackingForClients);
    if (sd.coldRepairWarrantyDuration) this.coldRepairWarrantyDuration.set(sd.coldRepairWarrantyDuration);
    if (sd.numberOfScanners != null) this.numberOfScanners.set(sd.numberOfScanners);
    if (sd.hasMobileRepairService !== undefined) this.hasMobileRepairService.set(sd.hasMobileRepairService);
    if (sd.numberOfAffiliatedBodyShops != null) this.numberOfAffiliatedBodyShops.set(sd.numberOfAffiliatedBodyShops);

    // Section 3
    if (sd.agencyContactEmail) this.agencyContactEmail.set(sd.agencyContactEmail);
    if (sd.agencyContactPhone) this.agencyContactPhone.set(sd.agencyContactPhone);
    if (sd.managementContactEmail) this.managementContactEmail.set(sd.managementContactEmail);
    if (sd.managementContactPhone) this.managementContactPhone.set(sd.managementContactPhone);
    if (sd.surveyOfficeContactEmail) this.surveyOfficeContactEmail.set(sd.surveyOfficeContactEmail);
    if (sd.surveyOfficeContactPhone) this.surveyOfficeContactPhone.set(sd.surveyOfficeContactPhone);
    if (sd.thirdPartyCommercialEmail) this.thirdPartyCommercialEmail.set(sd.thirdPartyCommercialEmail);
    if (sd.claimAssignmentNotificationEmail) this.claimAssignmentNotificationEmail.set(sd.claimAssignmentNotificationEmail);

    // Check section status and mark step as completed ONLY if FILLED
    if (sd.specificDataSectionStatus === 'FILLED') {
      console.log('✅ Specific data section is FILLED - marking step as completed');
      this.professionalsWizardService.markStepCompleted(1, true);
    } else {
      console.log('⚠️ Specific data section is NOT filled (status:', sd.specificDataSectionStatus, ') - marking step as NOT completed');
      this.professionalsWizardService.markStepCompleted(1, false);
    }
    
    // Save wizard state to persist the completed status
    this.professionalsWizardService.saveState();

    // Collaborators (at root level, not in specificData)
    const collaborators = data.collaborators || [];
    if (collaborators.length > 0) {
      const collabs = collaborators.map((c: any, index: number) => ({
        id: index,
        surname: c.lastName || '',
        name: c.firstName || '',
        fiscalCode: c.fiscalCode || '',
        registrationNumber: c.professionalRegistrationNumber || '',
        claimsSystemAccess: c.needsClaimsPortalAccess || false,
      }));
      this.collaborators.set(collabs);
      this.collaboratorIdCounter = collabs.length;
    }
  }

  buildPayload(status: 'DRAFT' | 'FILLED' = 'DRAFT'): any {
    const payload = {
      specificData: {
        // Section status
        specificDataSectionStatus: status,
        
        // Section 1
        companiesWorkedFor: this.companiesWorkedFor() || null,
        synergyInternationalCompanies: this.synergyInternationalCompanies(),
        synergyNationalCompanies: this.synergyNationalCompanies(),
        numberOfBranches: this.numberOfBranches(),
        isTeslaApproved: this.isTeslaApproved(),
        electricNetworkPercentage: this.electricNetworkPercentage(),
        numberOfOwnedBodyShops: this.numberOfOwnedBodyShops(),
        numberOfEmployees: this.numberOfEmployees(),
        numberOfHailTechnicians: this.numberOfHailTechnicians(),
        hasTrainingCourses: this.hasTrainingCourses(),
        hasEstimateSoftware: this.hasEstimateSoftware(),
        hasInternalPartsProcurement: this.hasInternalPartsProcurement(),
        operativeTerritory: this.operativeTerritory().join(','),

        // Section 2
        hasCreditAssignment: this.hasCreditAssignment(),
        hasMinimumAbsorption: this.hasMinimumAbsorption(),
        hasDeductibleAbsorption: this.hasDeductibleAbsorption(),
        hasPriorityGlassRepair: this.hasPriorityGlassRepair(),
        hasGlassRepairPartner: this.hasGlassRepairPartner(),
        glassRepairPartnerName: this.glassRepairPartnerName() || null,
        hasPickupDelivery: this.hasPickupDelivery(),
        hasTowTruck: this.hasTowTruck(),
        hasFreeReplacementCar: this.hasFreeReplacementCar(),
        hasInteriorCleaning: this.hasInteriorCleaning(),
        hasExteriorCleaning: this.hasExteriorCleaning(),
        hasSanitization: this.hasSanitization(),
        hasRepairTrackingForAgencies: this.hasRepairTrackingForAgencies(),
        hasRepairTrackingForClients: this.hasRepairTrackingForClients(),
        coldRepairWarrantyDuration: this.coldRepairWarrantyDuration() || null,
        numberOfScanners: this.numberOfScanners(),
        hasMobileRepairService: this.hasMobileRepairService(),
        numberOfAffiliatedBodyShops: this.numberOfAffiliatedBodyShops(),

        // Section 3
        agencyContactEmail: this.agencyContactEmail() || null,
        agencyContactPhone: this.agencyContactPhone() || null,
        managementContactEmail: this.managementContactEmail() || null,
        managementContactPhone: this.managementContactPhone() || null,
        surveyOfficeContactEmail: this.surveyOfficeContactEmail() || null,
        surveyOfficeContactPhone: this.surveyOfficeContactPhone() || null,
        thirdPartyCommercialEmail: this.thirdPartyCommercialEmail() || null,
        claimAssignmentNotificationEmail: this.claimAssignmentNotificationEmail() || null,
      },
    };

    console.log('📤 Hail Provider form payload:', payload);
    return payload;
  }

  saveDraft(): void {
    console.log('💾 Saving hail provider draft...');

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fiduciario non trovato',
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload('DRAFT');

    this.professionalsService
      .updateHailProviderData(professionalId, payload)
      .subscribe({
        next: () => {
          this.isSaving.set(false);

          this.messageService.add({
            severity: 'success',
            summary: 'Salvato',
            detail: 'Bozza salvata con successo',
          });
          this.liveRegionMessage.set('Bozza salvata con successo');
          this.onSaveDraft.emit(payload);
        },
        error: (err) => {
          this.isSaving.set(false);
          console.error('Error saving draft:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio della bozza',
          });
        },
      });
  }

  saveAndProceed(): void {
    console.log('💾 Saving and validating hail provider form...');
    this.formSubmitted.set(true);

    // Validate all required fields
    if (!this.isFormValid()) {
      this.validationFailed.emit([{
        title: 'Dati provider grandine',
        errors: [{ field: 'Campi obbligatori' }],
      }]);
      this.liveRegionMessage.set('Form non valido. Compila tutti i campi obbligatori');
      
      // Focus the error summary so screen readers announce it immediately (WCAG 2.4.3, 3.3.1)
      setTimeout(() => {
        const errorSummary = document.getElementById('form-error-summary');
        if (errorSummary) {
          errorSummary.focus();
          errorSummary.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
      return;
    }

    const professionalId = this.mockAuthService.getDemoProfessionalId();

    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fiduciario non trovato',
      });
      return;
    }

    this.isSaving.set(true);
    const payload = this.buildPayload('FILLED');

    this.professionalsService
      .updateHailProviderData(professionalId, payload)
      .subscribe({
        next: () => {
          this.isSaving.set(false);

          this.messageService.add({
            severity: 'success',
            summary: 'Completato',
            detail: 'Dati specifici salvati con successo',
          });
          this.liveRegionMessage.set('Form compilato e salvato con successo');
          this.onSave.emit(payload);
        },
        error: (err) => {
          this.isSaving.set(false);
          console.error('Error saving form:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Errore nel salvataggio dei dati',
          });
        },
      });
  }
}
